<!-- Header -->
<div class="generator-header">
  <div class="header-left">
    <div class="header-icon">
      <dojo-icon icon="wand-magic-sparkles" [size]="14" faPrefix="fas" />
    </div>
    <h2 class="header-title">Data Generator</h2>
  </div>
  <dojo-icon-button icon="xmark" size="xs" [transparent]="true" (click)="closePanel.emit()" />
</div>

<!-- Accordion -->
<mat-accordion displayMode="flat" class="generator-accordion">
  <!-- Library Panel -->
  <mat-expansion-panel
    class="generator-panel"
    [expanded]="libraryExpanded()"
    (opened)="libraryExpanded.set(true)"
    (closed)="libraryExpanded.set(false)"
  >
    <mat-expansion-panel-header>
      <mat-panel-title>
        <dojo-icon icon="book-open" [size]="13" />
        Library
      </mat-panel-title>
    </mat-expansion-panel-header>

    <ng-template matExpansionPanelContent>
      <div class="library-list">
        @for (dataset of libraryDatasets; track dataset.id) {
          <button class="library-item" (click)="onLoadDataset(dataset)">
            <div class="library-item-icon">
              <dojo-icon [icon]="dataset.icon" [size]="13" />
            </div>
            <div class="library-item-info">
              <span class="library-item-name">{{ dataset.name }}</span>
              <span class="library-item-meta">{{ dataset.description }}</span>
            </div>
            <span class="library-item-badge">n={{ dataset.values.length }}</span>
          </button>
        }
      </div>
    </ng-template>
  </mat-expansion-panel>

  <!-- Custom Panel -->
  <mat-expansion-panel
    class="generator-panel"
    [expanded]="customExpanded()"
    (opened)="customExpanded.set(true)"
    (closed)="customExpanded.set(false)"
  >
    <mat-expansion-panel-header>
      <mat-panel-title>
        <dojo-icon icon="sliders" [size]="13" />
        Custom
      </mat-panel-title>
    </mat-expansion-panel-header>

    <ng-template matExpansionPanelContent>
      <!-- Distribution Shape -->
      <section class="section">
        <div class="section-label mb-1">DISTRIBUTION SHAPE</div>
        <div class="distribution-grid">
          @for (card of distributionCards; track card.shape) {
            <button
              class="distribution-card"
              [class.selected]="distribution() === card.shape"
              (click)="selectDistribution(card.shape)"
            >
              <svg viewBox="0 0 60 30" class="distribution-icon">
                <path
                  [attr.d]="card.svgPath"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              <span class="distribution-label">{{ card.label }}</span>
            </button>
          }
        </div>
      </section>

      <!-- Sample Size -->
      <section class="section">
        <div class="section-label">
          SAMPLE SIZE
          <span class="sample-size-value">{{ sampleSize() }}</span>
        </div>
        <input
          type="range"
          class="sample-slider"
          min="1"
          max="500"
          step="1"
          [value]="sampleSize()"
          [style.--slider-progress]="sliderProgress()"
          (input)="onSampleSizeSlider($event)"
        />
        <div class="preset-row">
          @for (n of sampleSizePresets; track n) {
            <dojo-button
              [size]="'s'"
              [fill]="sampleSize() === n ? 'outline' : 'clear'"
              [customColor]="sampleSize() === n ? 'sky' : undefined"
              (click)="setSampleSize(n)"
            >
              n={{ n }}
            </dojo-button>
          }
        </div>
      </section>

      <!-- Parameters -->
      <section class="section">
        @if (isBimodal()) {
          <div class="param-row">
            <div class="param-field">
              <div class="section-label">CENTER 1</div>
              <dojo-number-input [formCtrl]="center1Ctrl" [compact]="true" />
            </div>
            <div class="param-field">
              <div class="section-label">CENTER 2</div>
              <dojo-number-input [formCtrl]="center2Ctrl" [compact]="true" />
            </div>
            <div class="param-field">
              <div class="section-label">SPREAD</div>
              <dojo-number-input [formCtrl]="spreadCtrl" [compact]="true" />
            </div>
          </div>
        } @else if (usesMeanSd()) {
          <div class="param-row">
            <div class="param-field">
              <div class="section-label">MEAN <span class="no-uppercase">(μ)</span></div>
              <dojo-number-input [formCtrl]="meanCtrl" [compact]="true" />
            </div>
            <div class="param-field">
              <div class="section-label">STD. DEVIATION <span class="no-uppercase">(σ)</span></div>
              <dojo-number-input [formCtrl]="stdDevCtrl" [compact]="true" />
            </div>
          </div>
        } @else {
          <div class="param-row">
            <div class="param-field">
              <div class="section-label">MIN VALUE</div>
              <dojo-number-input [formCtrl]="minValueCtrl" [compact]="true" />
            </div>
            <div class="param-field">
              <div class="section-label">MAX VALUE</div>
              <dojo-number-input [formCtrl]="maxValueCtrl" [compact]="true" />
            </div>
          </div>
        }
      </section>

      <!-- Outliers + Decimal Places -->
      <section class="section">
        <div class="dual-section">
          <div class="sub-section">
            <div class="section-label mb-1">OUTLIERS</div>
            <div class="stepper">
              <dojo-button size="s" fill="outline" (click)="decrementOutliers()">
                <dojo-icon icon="minus" [size]="12" />
              </dojo-button>
              <span class="stepper-value">{{ outliers() }}</span>
              <dojo-button size="s" fill="outline" (click)="incrementOutliers()">
                <dojo-icon icon="plus" [size]="12" />
              </dojo-button>
            </div>
          </div>
          <div class="sub-section">
            <div class="section-label mb-1">DECIMAL PLACES</div>
            <div class="preset-row">
              @for (dp of decimalPlacesOptions; track dp) {
                <dojo-button
                  [size]="'s'"
                  [fill]="decimalPlaces() === dp ? 'outline' : 'clear'"
                  [customColor]="decimalPlaces() === dp ? 'sky' : undefined"
                  (click)="setDecimalPlaces(dp)"
                >
                  {{ dp }}
                </dojo-button>
              }
            </div>
          </div>
        </div>
      </section>

      <!-- Advanced Options -->
      <section class="section advanced-section">
        <button class="advanced-toggle" (click)="toggleAdvanced()">
          <dojo-icon [icon]="advancedOpen() ? 'chevron-down' : 'chevron-right'" [size]="12" />
          Advanced options
        </button>

        @if (advancedOpen()) {
          <div class="advanced-content">
            <div class="advanced-field">
              <div class="section-label">SEED (OPTIONAL)</div>
              <dojo-text-input [formCtrl]="seedCtrl" placeholder="e.g. exam2024" [compact]="true" />
              <span class="field-hint">Same seed = same data every time</span>
            </div>

            <div class="advanced-field">
              <div class="section-label">COLUMN LABEL</div>
              <dojo-text-input [formCtrl]="columnLabelCtrl" placeholder="x" [compact]="true" />
              <span class="field-hint">Name for the data column</span>
            </div>
          </div>
        }
      </section>

      <!-- Generate Button -->
      <div class="generate-action">
        <dojo-button [size]="'l'" [fill]="'solid'" customColor="sky" (click)="onGenerate()">
          <dojo-icon icon="wand-magic-sparkles" [size]="14" faPrefix="fas" />
          {{ generateLabel() }}
        </dojo-button>
      </div>
    </ng-template>
  </mat-expansion-panel>
</mat-accordion>
